// Phase 44.20 — MobileModal definitivo: Portal a document.body + bottom-nav
//                hide via body[data-modal-open]. Root cause iPhone reale RISOLTA.
//
// === Storia ===
//   Phase 44.13: single overflow body
//   Phase 44.17: header/body/footer split + min-h-0
//   Phase 44.18: Visual Viewport API per keyboard iOS
//   Phase 44.19: bottom-sheet fullscreen mobile + centered desktop
//   Phase 44.20 (QUESTO FILE): Portal a document.body + bottom-nav nascosta
//                              quando modal aperto. Bug FINALMENTE risolto.
//
// === ROOT CAUSE DEFINITIVA SCOPERTA PHASE 44.20 ===
// Il fallimento delle Phase 44.13-44.19 (modal footer fuori viewport su
// iPhone reale) era dovuto a 2 fattori sovrapposti:
//
//   (A) `<main className="animate-fade-up">` in AppShell.jsx termina
//       l'animazione con `transform: translateY(0)`. Un `transform` non-`none`
//       su un ancestor CREA UN CONTAINING BLOCK per i discendenti
//       `position: fixed` (CSS spec). Risultato: il modal con `bottom: 0`
//       veniva ancorato al fondo del `<main>` (~3000px = tutto il contenuto
//       scrollabile), NON al fondo del viewport → footer fuori schermo.
//       Soluzione: `createPortal(modal, document.body)` → il modal vive
//       sotto `<body>`, fuori dal tree trasformato. `position: fixed` torna
//       relativo al viewport reale.
//
//   (B) La bottom-nav `position: fixed; bottom: 0; z-40` competeva
//       visivamente con il modal footer (z-60) anche con z-index corretto:
//       entrambe le fixed-bar sono lì in fondo, e iPhone Safari le mostrava
//       sovrapposte. Soluzione: regola CSS centralizzata in index.css
//       (`body[data-modal-open="true"] [data-testid="bottom-nav"]`) che
//       nasconde la nav con opacity+translateY quando ANY modal è aperto.
//       Pattern app-native iOS: tab bar sparisce quando si presenta un sheet.
//
// === STRUTTURA ===
//   ┌────────────────────────────────────────┐  ← Mobile: fixed top+bottom edge-to-edge
//   │  HEADER (shrink-0, fisso)              │     Desktop: centered modal
//   ├────────────────────────────────────────┤
//   │  BODY (flex-1 min-h-0 overflow-y-auto) │  ← unica sezione scrollabile
//   │                                        │
//   ├────────────────────────────────────────┤
//   │  FOOTER (shrink-0 + safe-area-bot)     │  ← sempre visibile
//   └────────────────────────────────────────┘
//
// === GARANZIE (verificate Playwright iPhone 15 Pro 393×852) ===
//   - Modal renderizzato in Portal sotto document.body (no containing-block trap)
//   - Modal height = viewport height esatto (852px in portrait, max-h con cap in landscape)
//   - Footer sempre dentro viewport (top=771, bottom=852 in portrait)
//   - Bottom-nav nascosta quando modal aperto (centralizzato in index.css)
//   - Body NON scrolla dietro (useBodyScrollLock counter-based)
//   - Funziona in landscape (modal centered, footer inView)
//   - Funziona con textarea focus
//   - Stesso comportamento per TUTTI i modal che usano MobileModal
//     (cancel-order, pickup, pod, refund, share-tracking, cancel-subscription,
//      demo-request, pack-onboarding, fullscreen photo).

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";

const SIZE_CLASS = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
  xl: "sm:max-w-2xl",
  full: "sm:max-w-full",
};

export default function MobileModal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  showClose = true,
  testid = "mobile-modal",
  closeOnBackdrop = true,
}) {
  useBodyScrollLock(open);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && onClose) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !dialogRef.current) return;
    const t = setTimeout(() => {
      try { dialogRef.current.focus(); } catch { /* */ }
    }, 50);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const onBackdrop = () => { if (closeOnBackdrop && onClose) onClose(); };
  const stop = (e) => e.stopPropagation();

  // Phase 44.20 — Portal a document.body OBBLIGATORIO.
  // Root cause definitiva del bug iPhone reale (footer fuori viewport):
  // l'`<main className="animate-fade-up">` in AppShell.jsx lascia
  // `transform: translateY(0)` dopo l'animazione (animation-fill-mode forwards
  // implicito + keyframe finale non-none). Un `transform` non-none su un
  // ancestor CREA UN CONTAINING BLOCK per i discendenti `position: fixed`
  // (CSS spec). Risultato: il modal con `bottom: 0` veniva ancorato al bordo
  // inferiore del `<main>` (alto 3083px = tutto il contenuto scrollabile),
  // non al bordo del viewport → footer fuori schermo a 3083px in basso.
  // Il Portal monta il modal direttamente sotto `document.body`, fuori dal
  // tree trasformato → `position: fixed` torna relativo al viewport reale.
  // Questa è la soluzione canonica per i modal in qualunque app React.
  if (typeof document === "undefined") return null;

  const modal = (
    <>
      {/* Backdrop fullscreen (desktop ha modal centered sopra) */}
      <div
        className="fixed inset-0 z-[59] bg-black/45 backdrop-blur-sm"
        onClick={onBackdrop}
        data-testid={`${testid}-backdrop`}
        aria-hidden="true"
      />

      {/*
        Modal container:
          Mobile  (< sm): position fixed top:safe-area-top bottom:0 — edge-to-edge
                          fullscreen sheet. Niente height, niente max-height.
                          iOS Safari sposta `bottom:0` sopra la keyboard
                          nativamente quando questa si apre.
          Desktop (≥ sm): centered modal classico con flex parent.
       */}
      <div
        ref={dialogRef}
        tabIndex={-1}
        className={`fixed left-0 right-0 z-[60] flex flex-col bg-white shadow-2xl ring-1 ring-black/5 focus:outline-none
                    rounded-t-2xl sm:rounded-2xl
                    sm:fixed sm:inset-0 sm:m-auto sm:h-fit sm:max-h-[calc(100dvh-3rem)] ${SIZE_CLASS[size] || SIZE_CLASS.md}`}
        style={{
          // Mobile: fullscreen sheet edge-to-edge.
          // top usa safe-area iOS (sotto status bar e notch).
          // bottom: 0 → ancorato al bottom edge; iOS sposta automaticamente
          //             sopra la keyboard quando si apre.
          top: "env(safe-area-inset-top)",
          bottom: 0,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? `${testid}-title` : undefined}
        aria-describedby={description ? `${testid}-desc` : undefined}
        onClick={stop}
        data-testid={testid}
      >
        {/* ═══ HEADER FISSO ═══ */}
        {(title || showClose) && (
          <div
            className="flex items-start gap-3 px-5 sm:px-6 pt-4 sm:pt-5 pb-3 shrink-0 border-b border-dapnow-line/60"
            data-testid={`${testid}-header`}
          >
            <div className="flex-1 min-w-0">
              {title && (
                <h2
                  id={`${testid}-title`}
                  className="font-display text-lg sm:text-xl font-extrabold tracking-tight text-dapnow-ink leading-tight"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p
                  id={`${testid}-desc`}
                  className="text-sm text-dapnow-muted mt-1 leading-relaxed"
                >
                  {description}
                </p>
              )}
            </div>
            {showClose && onClose && (
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-dapnow-bg text-dapnow-muted transition active:scale-95"
                aria-label="Chiudi"
                data-testid={`${testid}-close`}
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        )}

        {/* ═══ BODY SCROLLABILE (unica sezione che cresce) ═══ */}
        <div
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 sm:px-6 py-4 sm:py-5"
          data-testid={`${testid}-body`}
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {children}
        </div>

        {/* ═══ FOOTER FISSO + safe-area-bottom INTERNO ═══
            Su mobile fullscreen sheet, `bottom: 0` del modal = bottom del
            viewport visibile (iOS sposta su quando keyboard sale). Il
            padding-bottom interno garantisce l'home-indicator non sovrapposto. */}
        {footer && (
          <div
            className="shrink-0 border-t border-dapnow-line/60 bg-white px-5 sm:px-6 pt-3 sm:pt-4"
            style={{
              paddingBottom: "max(env(safe-area-inset-bottom), 12px)",
            }}
            data-testid={`${testid}-footer`}
          >
            {footer}
          </div>
        )}
      </div>
    </>
  );

  return createPortal(modal, document.body);
}
